import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    pendingQuestions: 0,
    totalQuestions: 0,
    totalUsers: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchAdminStats();
    } else if (user) {
      // Redirect non-admin users
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchAdminStats = async () => {
    try {
      // Fetch pending questions count
      const pendingRes = await axios.get(`${API}/teacher/pending-questions`, { withCredentials: true });
      
      setStats({
        pendingQuestions: pendingRes.data?.count || 0,
        totalQuestions: 331, // From database
        totalUsers: 0,
        recentActivity: []
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const adminSections = [
    {
      id: 'questions',
      icon: '📝',
      title: language === 'tr' ? 'Soru Yönetimi' : 'Question Management',
      description: language === 'tr' ? 'Bekleyen soruları onayla veya reddet' : 'Approve or reject pending questions',
      count: stats.pendingQuestions,
      path: '/admin/approval',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'question-bank',
      icon: '📚',
      title: language === 'tr' ? 'Soru Bankası' : 'Question Bank',
      description: language === 'tr' ? 'Tüm onaylanmış soruları görüntüle' : 'View all approved questions',
      count: stats.totalQuestions,
      path: '/question-bank',
      gradient: 'from-teal-500 to-green-500'
    },
    {
      id: 'users',
      icon: '👥',
      title: language === 'tr' ? 'Kullanıcı Yönetimi' : 'User Management',
      description: language === 'tr' ? 'Kullanıcıları görüntüle ve yönet' : 'View and manage users',
      count: stats.totalUsers,
      path: '/admin/users',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'teachers',
      icon: '👨‍🏫',
      title: language === 'tr' ? 'Öğretmen Başvuruları' : 'Teacher Applications',
      description: language === 'tr' ? 'Mentor başvurularını incele' : 'Review mentor applications',
      count: 0,
      path: '/admin/applications',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      id: 'analytics',
      icon: '📊',
      title: language === 'tr' ? 'Platform Analitikleri' : 'Platform Analytics',
      description: language === 'tr' ? 'Kullanım istatistiklerini gör' : 'View usage statistics',
      path: '/admin/analytics',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      id: 'settings',
      icon: '⚙️',
      title: language === 'tr' ? 'Sistem Ayarları' : 'System Settings',
      description: language === 'tr' ? 'Platform ayarlarını düzenle' : 'Edit platform settings',
      path: '/admin/settings',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      id: 'question-generation',
      icon: '🤖',
      title: language === 'tr' ? 'AI Soru Üretimi' : 'AI Question Generation',
      description: language === 'tr' ? 'TYT-AP soru çiftleri oluştur' : 'Generate TYT-AP question pairs',
      path: '/admin/question-generation',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      id: 'bulk-upload',
      icon: '📤',
      title: language === 'tr' ? 'Toplu Soru Yükleme' : 'Bulk Question Upload',
      description: language === 'tr' ? 'CSV ile toplu soru ekle' : 'Upload questions via CSV',
      path: '/admin/bulk-upload',
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      id: 'digital-library',
      icon: '📚',
      title: language === 'tr' ? 'Dijital Kütüphane' : 'Digital Library',
      description: language === 'tr' ? '3. parti yayıncı içerikleri' : 'Third-party publisher content',
      path: '/admin/library',
      gradient: 'from-violet-500 to-purple-500'
    },
    {
      id: 'registration',
      icon: '🔬',
      title: language === 'tr' ? 'Kayıt Tanılama' : 'Registration Diagnostics',
      description: language === 'tr' ? 'Kayıt akışı sorunlarını tespit et' : 'Diagnose registration flow issues',
      path: '/admin/registration-diagnostics',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'content',
      icon: '📑',
      title: language === 'tr' ? 'İçerik Yönetimi' : 'Content Management',
      description: language === 'tr' ? 'Soru bankası ve materyaller' : 'Question bank and materials',
      count: stats.totalQuestions,
      path: '/question-bank',
      gradient: 'from-pink-500 to-rose-500'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Admin Paneli' : 'Admin Panel'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                👑 {language === 'tr' ? 'Hoş Geldiniz, Admin' : 'Welcome, Admin'}
              </h1>
              <p className="text-orange-100 text-lg">
                {language === 'tr' 
                  ? 'Sistem yönetimi ve kontrol paneli'
                  : 'System management and control panel'}
              </p>
            </div>
            <div className="text-6xl">⚙️</div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📝</span>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Bekliyor' : 'Pending'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{stats.pendingQuestions}</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Onay Bekleyen Soru' : 'Pending Questions'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📚</span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Aktif' : 'Active'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{stats.totalQuestions}</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Toplam Soru' : 'Total Questions'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">🤖</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                AI
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">82</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Dual-Track Soru' : 'Dual-Track Questions'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">👥</span>
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Sistem' : 'System'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">-</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Aktif Kullanıcı' : 'Active Users'}</div>
          </div>
        </div>

        {/* Admin Sections */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {language === 'tr' ? '⚡ Hızlı Erişim' : '⚡ Quick Access'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminSections.map((section) => (
              <div
                key={section.id}
                onClick={() => navigate(section.path)}
                className="group bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`inline-flex p-4 bg-gradient-to-br ${section.gradient} rounded-2xl text-4xl shadow-lg`}>
                    {section.icon}
                  </div>
                  {section.count !== undefined && (
                    <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                      {section.count}
                    </div>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                  {section.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-4">
                  {section.description}
                </p>
                
                <div className="flex items-center text-blue-600 font-semibold">
                  <span>{language === 'tr' ? 'Yönet' : 'Manage'}</span>
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            🔧 {language === 'tr' ? 'Sistem Durumu' : 'System Status'}
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                <span className="font-medium text-gray-800">{language === 'tr' ? 'Backend API' : 'Backend API'}</span>
              </div>
              <span className="text-green-600 font-semibold">{language === 'tr' ? 'Çalışıyor' : 'Running'}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                <span className="font-medium text-gray-800">{language === 'tr' ? 'Veritabanı' : 'Database'}</span>
              </div>
              <span className="text-green-600 font-semibold">{language === 'tr' ? 'Bağlı' : 'Connected'}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                <span className="font-medium text-gray-800">{language === 'tr' ? 'AI Servisler' : 'AI Services'}</span>
              </div>
              <span className="text-green-600 font-semibold">{language === 'tr' ? 'Aktif' : 'Active'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
